/**
 * 회사별 규칙 가져오기 미리보기 모달 — 로컬 ↔ 운영 동기화.
 *
 * 내보내기로 받은 JSON 파일을 올리면, 이 서버와 비교해 바뀌는 회사·규칙을
 * 전/후로 보여 주고 선택한 회사만 반영한다(파일에 없는 회사는 건드리지 않음).
 */

import { useEffect, useState } from "react";
import { AlertTriangle, CheckCircle2, Loader2, Upload, X } from "lucide-react";
import { useTranslation } from "react-i18next";

import type { AgentRuleField, AgentRulesChange, AgentRulesFile } from "@/lib/api/agents";
import { ApiError } from "@/lib/api/client";

import { useAgentRulesImportPreview, useApplyAgentRulesImport } from "./useAgents";

interface Props {
  file: AgentRulesFile;
  onClose: () => void;
}

function RulesJson({ value }: { value: Record<string, unknown> | null | undefined }) {
  const { t } = useTranslation();
  if (!value) {
    return (
      <p className="text-[11px] italic text-slate-400">{t("admin.agents.rulesTransfer.empty")}</p>
    );
  }
  return (
    <pre className="max-h-48 overflow-auto whitespace-pre-wrap break-all rounded bg-slate-50 p-2 font-mono text-[11px] text-slate-700">
      {JSON.stringify(value, null, 2)}
    </pre>
  );
}

function ChangeRow({
  change,
  checked,
  onToggle,
}: {
  change: AgentRulesChange;
  checked: boolean;
  onToggle: () => void;
}) {
  const { t } = useTranslation();
  return (
    <li className="space-y-2 px-3 py-3">
      <label className="flex cursor-pointer items-center gap-2 text-xs">
        <input type="checkbox" checked={checked} onChange={onToggle} />
        <span className="font-mono font-medium text-slate-800">{change.agent_id}</span>
        <span className="text-slate-700">{change.official_name}</span>
      </label>
      {change.file_official_name ? (
        <p className="flex items-center gap-1 text-[11px] text-amber-700">
          <AlertTriangle className="h-3 w-3" />
          {t("admin.agents.rulesTransfer.nameMismatch", { name: change.file_official_name })}
        </p>
      ) : null}
      {change.fields.map((field: AgentRuleField) => (
        <div key={field} className="space-y-1">
          <p className="text-[11px] font-semibold text-slate-600">
            {t(`admin.agents.rulesTransfer.fields.${field}`)}
          </p>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <p className="mb-0.5 text-[10px] text-slate-400">
                {t("admin.agents.rulesTransfer.current")}
              </p>
              <RulesJson value={change.before[field]} />
            </div>
            <div>
              <p className="mb-0.5 text-[10px] text-blue-600">
                {t("admin.agents.rulesTransfer.incoming")}
              </p>
              <RulesJson value={change.after[field]} />
            </div>
          </div>
        </div>
      ))}
    </li>
  );
}

export default function AgentRulesImportModal({ file, onClose }: Props) {
  const { t } = useTranslation();
  const preview = useAgentRulesImportPreview(file);
  const apply = useApplyAgentRulesImport();
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [appliedCount, setAppliedCount] = useState<number | null>(null);

  const changes = preview.data?.changes ?? [];

  useEffect(() => {
    setSelected(new Set((preview.data?.changes ?? []).map((c) => c.agent_id)));
  }, [preview.data]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const toggle = (agentId: string) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(agentId)) next.delete(agentId);
      else next.add(agentId);
      return next;
    });

  const handleApply = () => {
    apply.mutate(
      { file, agentIds: [...selected] },
      { onSuccess: (res) => setAppliedCount(res.applied.length) },
    );
  };

  const errorOf = (err: Error | null) =>
    err instanceof ApiError ? err.detail : (err?.message ?? null);
  const error = errorOf(preview.error) ?? errorOf(apply.error);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 px-4 py-8"
      onClick={onClose}
    >
      <div
        className="w-full max-w-4xl rounded-lg bg-white shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="flex items-center justify-between border-b border-slate-200 px-5 py-3">
          <h2 className="text-sm font-semibold text-slate-900">
            {t("admin.agents.rulesTransfer.previewTitle")}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="rounded p-1 text-slate-400 transition hover:bg-slate-100 hover:text-slate-700"
            aria-label={t("common.actions.close")}
          >
            <X className="h-4 w-4" />
          </button>
        </header>

        <div className="max-h-[70vh] space-y-3 overflow-y-auto px-5 py-4">
          <p className="text-xs text-slate-500">
            {t("admin.agents.rulesTransfer.fileInfo", {
              source: file.source ?? "-",
              exportedAt: file.exported_at ?? "-",
            })}
          </p>

          {error ? (
            <div
              role="alert"
              className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700"
            >
              {error}
            </div>
          ) : null}

          {appliedCount !== null ? (
            <div className="flex items-center gap-2 rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs text-emerald-700">
              <CheckCircle2 className="h-3.5 w-3.5" />
              {t("admin.agents.rulesTransfer.applied", { count: appliedCount })}
            </div>
          ) : null}

          {preview.isLoading ? (
            <p className="flex items-center gap-2 text-xs text-slate-500">
              <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
              {t("admin.agents.rulesTransfer.loading")}
            </p>
          ) : preview.data ? (
            <>
              <p className="text-xs font-medium text-slate-700">
                {t("admin.agents.rulesTransfer.summary", {
                  changes: preview.data.changes.length,
                  unchanged: preview.data.unchanged_count,
                  unknown: preview.data.unknown_ids.length,
                })}
              </p>
              {preview.data.unknown_ids.length > 0 ? (
                <p className="text-[11px] text-slate-500">
                  {t("admin.agents.rulesTransfer.unknownIds", {
                    ids: preview.data.unknown_ids.join(", "),
                  })}
                </p>
              ) : null}
              {changes.length === 0 ? (
                <p className="text-xs italic text-slate-500">
                  {t("admin.agents.rulesTransfer.noChanges")}
                </p>
              ) : (
                <ul className="divide-y divide-slate-200 rounded-md border border-slate-200">
                  {changes.map((change) => (
                    <ChangeRow
                      key={change.agent_id}
                      change={change}
                      checked={selected.has(change.agent_id)}
                      onToggle={() => toggle(change.agent_id)}
                    />
                  ))}
                </ul>
              )}
            </>
          ) : null}
        </div>

        <div className="flex justify-end gap-2 border-t border-slate-100 px-5 py-3">
          <button
            type="button"
            onClick={onClose}
            className="rounded-md border border-slate-300 px-3 py-1.5 text-sm text-slate-700 hover:bg-slate-50"
          >
            {t("common.actions.close")}
          </button>
          <button
            type="button"
            onClick={handleApply}
            disabled={selected.size === 0 || apply.isPending}
            className="inline-flex items-center gap-2 rounded-md bg-blue-600 px-3 py-1.5 text-sm font-semibold text-white shadow-sm transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:bg-slate-300"
          >
            {apply.isPending ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
            ) : (
              <Upload className="h-3.5 w-3.5" />
            )}
            {t("admin.agents.rulesTransfer.apply", { count: selected.size })}
          </button>
        </div>
      </div>
    </div>
  );
}
